import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createSightseen,
  deleteSightseenImage,
  getAllActiveSightseen,
  getAllSightseen,
  getSightseenByID,
  sightseenImageUpload,
  updateSightseen,
} from "./services/sightseen.services";
import {
  clearSightseenDetails,
  clearSightseenImageID,
  setSightseenImageID,
} from "./sightseenSlice";
import { Typography } from "@progress/kendo-react-common";
import { STATUSARRAY } from "../../_contstants/common";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { getAllActiveSectors } from "../sector/services/sector.services";
import { closed } from "../../components/drawer/drawerSlice";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormFileUpload from "../../components/formFields/FormFileUpload";
import { MdDelete } from "react-icons/md";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import IconComponent from "../../components/common/IconComponent";
import { useLocation, useNavigate } from "react-router-dom";
import ShadowCard from "../../components/common/ShadowCard";
import FormTextArea from "../../components/formFields/FormTextArea";
import { slugify } from "../../_helper/helper";

const CreateSightseen: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const SightseenID = location.state?.SightseenID;

  const SightseenDetail = useAppSelector(
    (state) => state.sightseen.SightseenDetail,
  );
  // const SightseenID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.sightseen.formLoading);
  const SectorList = useAppSelector((state) => state.sector.SectorList);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);
  const [formKey, setFormKey] = React.useState(0);

  useEffect(() => {
    dispatch(getAllActiveSectors());
    return () => {
      dispatch(clearSightseenDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (SightseenID) {
      dispatch(getSightseenByID(SightseenID));
    }
  }, [SightseenID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [SightseenDetail]);

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e?.value);
    formRenderProps.onChange("URL", { value: slug });
  };

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const SightSeenImage =
      values?.SightSeenImage && typeof values?.SightSeenImage !== "string"
        ? values.SightSeenImage[0].getRawFile()
        : "";
    const BannerImage =
      values?.BannerImage && typeof values?.BannerImage !== "string"
        ? values.BannerImage[0].getRawFile()
        : "";

    const formData = {
      SectorId: values?.SectorId,
      SightseenName: values?.SightseenName,
      URL: values?.URL,
      Description: values?.Description,
      ShortDescription: values?.ShortDescription,
      MetaTitle: values.MetaTitle ?? "",
      MetaKeywords: values.MetaKeywords ?? "",
      MetaDescription: values.MetaDescription ?? "",
      Rate: values?.Rate ? +values?.Rate : null,
      IsActive: values?.IsActive === 1,
    };
    try {
      if (!SightseenID) {
        const response = await dispatch(createSightseen(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values?.SightSeenImage &&
              typeof values?.SightSeenImage !== "string") ||
              (values?.BannerImage &&
                typeof values?.BannerImage !== "string") ||
              values.attachments?.length > 0) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("SightSeenImage", SightSeenImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("ID", response?.payload?.Data?.ID);
            if (values?.attachments && Array.isArray(values.attachments)) {
              values.attachments.forEach((image: any) => {
                if (image.attachment && typeof image.attachment !== "string")
                  return;
                const attachment =
                  image instanceof File ? image : image?.getRawFile();
                if (attachment) {
                  ImageData.append("attachment", attachment);
                }
              });
            }

            await dispatch(sightseenImageUpload(ImageData));
          }
          dispatch(closed());
          dispatch(clearSightseenDetails());
          navigate(-1);
          if (isIndirect) {
            dispatch(getAllActiveSightseen());
          } else {
            dispatch(getAllSightseen());
          }
        }
      } else {
        const editPayload = {
          ...formData,
          ID: SightseenID,
        };
        const response = await dispatch(updateSightseen(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values.SightSeenImage &&
              typeof values.SightSeenImage !== "string") ||
            (values.BannerImage && typeof values.BannerImage !== "string") ||
            values.attachments?.length > 0
          ) {
            ImageData.append("SightSeenImage", SightSeenImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("ID", values?.ID ? values?.ID : "");
            if (values?.attachments && Array.isArray(values.attachments)) {
              values.attachments.forEach((image: any) => {
                if (image.attachment && typeof image.attachment !== "string")
                  return;
                const attachment =
                  image instanceof File ? image : image?.getRawFile();
                if (attachment) {
                  ImageData.append("attachment", attachment);
                }
              });
            }

            await dispatch(sightseenImageUpload(ImageData));
          }
          dispatch(closed());
          dispatch(clearSightseenDetails());
          dispatch(getAllSightseen());
          navigate(-1);
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
    navigate("/sightseen");
  };

  const handleDeleteImage = (ID: number) => {
    dispatch(openDialog("deleteSightseenImage"));
    dispatch(setSightseenImageID(ID));
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <DeleteSightseenImageDialog />
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={SightseenDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
                gap={{ rows: 10, cols: 10 }}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {SightseenID ? "Update Sightseen" : "Add Sightseen"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="SightseenName"
                    label="Sightseen"
                    placeholder="Sightseen"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                    onChange={(e) => handleCreateSlug(e, formRenderProps)}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="URL"
                    label="URL"
                    placeholder="URL"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                    onInput={(e: any) => {
                      e.target.value = slugify(e?.target?.value);
                    }}
                    onBlurCapture={(e: any) => {
                      if (e?.target?.value) {
                        formRenderProps.onChange("URL", {
                          value: slugify(e?.target?.value)?.replace(/-+$/, ""),
                        });
                      }
                    }}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"SectorId"}
                    name={"SectorId"}
                    placeholder={"Sector"}
                    component={FormSelectionField}
                    label={"Sector"}
                    validator={requiredValidator}
                    astrike={true}
                    options={SectorList?.map((sector: any) => {
                      return {
                        value: sector.ID,
                        label: sector.SectorName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="Rate"
                    label="Rate"
                    placeholder="0"
                    component={FormNumericTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"IsActive"}
                    name={"IsActive"}
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    label={"Status"}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status.value,
                        label: status.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Field
                    name="ShortDescription"
                    label="Short Description"
                    placeholder="Short Description"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Field
                    name="Description"
                    label="Description"
                    placeholder="Description"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="SightSeenImage"
                    type="file"
                    accept="image/*"
                    label="Sightseen Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="BannerImage"
                    type="file"
                    accept="image/*"
                    label="Banner Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    justifyContent="start"
                    name="attachments"
                    label="Attachment"
                    type="file"
                    accept="image/*"
                    alternateName="attachment"
                    component={FormFileUpload}
                    multiple
                  />
                </GridLayoutItem>
                {SightseenDetail?.Images &&
                  SightseenDetail?.Images?.length > 0 && (
                    <GridLayoutItem colSpan={3} className="mt-3">
                      <div className="card shadow-sm border-0">
                        {SightseenDetail?.Images?.length > 0 && (
                          <div
                            className="card-body p-2 pt-3 pe-3"
                            style={{
                              background: "#f8f9fa",
                              borderRadius: "8px",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h5
                                className="mb-0"
                                style={{
                                  color: "#2c3e50",
                                  fontWeight: "600",
                                  fontSize: "1.1rem",
                                }}
                              >
                                Uploaded Images
                              </h5>
                              <span className="badge bg-kendo-primary rounded-pill">
                                {SightseenDetail?.Images?.length || 0} files
                              </span>
                            </div>

                            <div className="d-flex flex-wrap gap-3">
                              {SightseenDetail?.Images?.map((image: any) => (
                                <div
                                  key={image.ID}
                                  className="position-relative"
                                  style={{
                                    width: "150px",
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  <div className="card h-100 border-0 shadow-sm overflow-hidden">
                                    <div className="ratio ratio-1x1 bg-light overflow-hidden">
                                      <div
                                        style={{
                                          position: "absolute",
                                          width: "100%",
                                          height: "auto",
                                          display: "flex",
                                          justifyContent: "space-between",
                                        }}
                                      >
                                        <Button
                                          themeColor={"error"}
                                          fillMode={"solid"}
                                          style={{
                                            position: "relative",
                                            padding: "5px 10px",
                                            width: 30,
                                            height: 30,

                                            color: "#fff",
                                            zIndex: "1",
                                            borderRadius: "0 0 0 5px",
                                          }}
                                          onClick={() =>
                                            handleDeleteImage(image.ID)
                                          }
                                        >
                                          <IconComponent
                                            icon={MdDelete}
                                            className="absolute-position fs-5"
                                          />
                                        </Button>
                                      </div>
                                      <img
                                        className="img-fluid object-fit-cover"
                                        src={image.Image}
                                        alt="Uploaded document"
                                        style={{
                                          cursor: "pointer",
                                          transition: "transform 0.3s",
                                        }}
                                        onMouseEnter={(e) =>
                                          (e.currentTarget.style.transform =
                                            "scale(1.05)")
                                        }
                                        onMouseLeave={(e) =>
                                          (e.currentTarget.style.transform =
                                            "scale(1)")
                                        }
                                        onClick={() =>
                                          window.open(
                                            `${image.Image}`,
                                            "_blank",
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </GridLayoutItem>
                  )}
                <GridLayoutItem colSpan={3}>
                  <Field
                    name="MetaTitle"
                    label="Meta Title"
                    placeholder="Meta Title"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="MetaKeywords"
                    label="Meta Keywords"
                    placeholder="Meta Keywords"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="MetaDescription"
                    label="Meta Description"
                    placeholder="Meta Description"
                    component={FormTextArea}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    textAlign: "end",
                    marginTop: "20px",
                    marginBottom: "20px",
                    width: "100%",
                  }}
                >
                  <ButtonWithLoading
                    label={SightseenID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || formLoading}
                    loading={formLoading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 10 }}
                    onClick={handleFormClose}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

const DeleteSightseenImageDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const location = useLocation();
  const SightseenID = location.state?.SightseenID;

  const SightseenImageID = useAppSelector(
    (state) => state.sightseen.SightseenImageID,
  );

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteSightseenImage(ID));
      if (response.meta.requestStatus === "fulfilled") {
        if (SightseenID) {
          dispatch(getSightseenByID(SightseenID));
        }
        dispatch(closeDialog());
        dispatch(clearSightseenImageID());
      } else {
        if (SightseenID) {
          dispatch(getSightseenByID(SightseenID));
        }
        dispatch(closeDialog());
        dispatch(clearSightseenImageID());
      }
    }
  };
  return (
    <>
      {dialogName === "deleteSightseenImage" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Image?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Image?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(SightseenImageID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default CreateSightseen;
